// Typed fetch wrappers for all FastAPI endpoints.
// Auth is Supabase-only: App.tsx calls setSupabaseToken() from onAuthStateChange,
// and every request below attaches `Authorization: Bearer <jwt>`.

import type {
  ConceptExplanation,
  Exercise,
  KidPreferences,
  LearnAskResponse,
  LearnHistoryItem,
  LearnSettings,
  LearningObjective,
  PagePayload,
  ParentChatTurn,
  ProgressAnalysis,
  SessionReport,
  StudentProfile,
  SubmitResponse,
} from './types';

// ── Supabase bearer token (set by App.tsx via onAuthStateChange) ──────────
let _supabaseToken: string | null = null;

export function setSupabaseToken(token: string | null): void {
  _supabaseToken = token;
}

export function getSupabaseToken(): string | null {
  return _supabaseToken;
}

// ── Parent-gate elevation token ──────────────────────────────────────────
// Set by ParentGate after a successful /verify-parent-gate call. Sent on
// sensitive endpoints (child-pin, child-profile, delete-account) via
// X-Parent-Elevation. Cleared on logout / when the backend rejects with 403.
let _elevationToken: string | null = null;
let _elevationExpiresAt: number | null = null;

export function setParentElevation(token: string | null, expiresAt: string | null): void {
  _elevationToken = token;
  _elevationExpiresAt = expiresAt ? Date.parse(expiresAt) : null;
}

export function clearParentElevation(): void {
  _elevationToken = null;
  _elevationExpiresAt = null;
}

function currentElevationToken(): string | null {
  if (!_elevationToken || !_elevationExpiresAt) return null;
  if (Date.now() >= _elevationExpiresAt) {
    _elevationToken = null;
    _elevationExpiresAt = null;
    return null;
  }
  return _elevationToken;
}

function sessionHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (_supabaseToken) headers['Authorization'] = `Bearer ${_supabaseToken}`;
  return headers;
}

function elevatedHeaders(): Record<string, string> {
  const headers = sessionHeaders();
  const tok = currentElevationToken();
  if (tok) headers['X-Parent-Elevation'] = tok;
  return headers;
}

export class ApiError extends Error {
  status: number;
  detail: string;
  constructor(status: number, detail: string) {
    super(detail);
    this.status = status;
    this.detail = detail;
    this.name = 'ApiError';
  }
}

function formatDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (d && typeof d === 'object' && 'msg' in d) {
          const loc = Array.isArray((d as { loc?: unknown }).loc)
            ? ((d as { loc: unknown[] }).loc).join('.')
            : '';
          return loc ? `${loc}: ${(d as { msg: string }).msg}` : String((d as { msg: string }).msg);
        }
        return String(d);
      })
      .filter(Boolean);
    return parts.length ? parts.join('; ') : fallback;
  }
  if (detail && typeof detail === 'object') {
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return fallback;
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const mergedHeaders = { ...sessionHeaders(), ...(init.headers as Record<string, string> || {}) };
  const res = await fetch(path, { ...init, headers: mergedHeaders });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const msg = formatDetail(body.detail, `API-Fehler ${res.status}`);
    throw new ApiError(res.status, msg);
  }
  return res.json() as Promise<T>;
}

// ── Parent-only requests with gate re-prompt ──────────────────────────
// Parent reads/writes need a fresh gate token (5-minute TTL). App.tsx
// registers a prompt that opens the ParentGate; on 403 we ask once and retry.
// Concurrent 403s (the dashboard loads several tabs) share one prompt.

type ElevationPrompt = (purpose: string) => Promise<boolean>;
let _elevationPrompt: ElevationPrompt | null = null;
let _promptInFlight: Promise<boolean> | null = null;

export function setElevationPrompt(fn: ElevationPrompt | null): void {
  _elevationPrompt = fn;
}

async function reconfirmParent(): Promise<boolean> {
  if (!_elevationPrompt) return false;
  if (!_promptInFlight) {
    _promptInFlight = _elevationPrompt('reconfirm').finally(() => { _promptInFlight = null; });
  }
  return _promptInFlight;
}

async function withParentRetry<T>(attempt: () => Promise<T>): Promise<T> {
  try {
    return await attempt();
  } catch (e) {
    if (!(e instanceof ApiError) || e.status !== 403) throw e;
    if (!(await reconfirmParent())) throw e;
    return attempt();
  }
}

function parentFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return withParentRetry(() => apiFetch<T>(path, {
    ...init,
    headers: { ...(init.headers as Record<string, string> || {}), ...elevatedHeaders() },
  }));
}

/** Download a parent-only binary (e.g. the summary PDF) with gate re-prompt. */
export function fetchParentBlob(path: string): Promise<Blob> {
  return withParentRetry(async () => {
    const res = await fetch(path, { headers: elevatedHeaders() });
    if (!res.ok) throw new ApiError(res.status, `API-Fehler ${res.status}`);
    return res.blob();
  });
}

/** Best-effort client-side telemetry ping. Never throws. */
export function reportClientEvent(event: string, payload: Record<string, unknown> = {}): void {
  try {
    fetch('/telemetry', {
      method: 'POST',
      headers: sessionHeaders(),
      body: JSON.stringify({ event, payload }),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* no-op */ }
}

// ── Auth ─────────────────────────────────────────────────────────────

export function fullExportProfileUrl(): string {
  return '/profile/full_export';
}

/** Trigger an authenticated JSON download — works around bare <a href> not
 * including the Supabase Bearer token / X-Parent-Elevation header. */
export async function downloadAuthenticatedJson(
  path: string,
  filenameHint: string,
): Promise<void> {
  const res = await fetch(path, { headers: elevatedHeaders() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    const msg = formatDetail((body as { detail?: unknown }).detail, `API-Fehler ${res.status}`);
    throw new ApiError(res.status, msg);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/.exec(dispo);
  const filename = (m && m[1]) || filenameHint;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Fetch a binary resource (e.g. milestone PNG) with the Supabase Bearer token. */
export async function fetchAuthenticatedBlob(path: string): Promise<Blob> {
  const res = await fetch(path, { headers: sessionHeaders() });
  if (!res.ok) throw new ApiError(res.status, `API-Fehler ${res.status}`);
  return res.blob();
}

/** Child-summary row used by v3 auth endpoints. */
export interface ChildSummary {
  id: string;
  name: string;
  klasse: number;
  gender: string | null;
  has_pin: boolean;
}

// ── Auth v3 (Supabase-JWT-backed) ────────────────────────────────────────

/** Fetch the logged-in parent's first child profile (called after supabase.auth.signInWithPassword). */
export async function getProfileV3(): Promise<{
  ok: boolean;
  family_id: string;
  child_id: string;
  profile: StudentProfile;
}> {
  return apiFetch('/auth/v3/profile');
}

/** List all children in the caller's family. */
export async function listChildrenV3(): Promise<{ children: ChildSummary[] }> {
  return apiFetch('/auth/v3/children');
}

/** Finish registration for a Supabase-invited user: set their family + first
 *  child after they've already authenticated via the invite magic link.
 *  The parent password is set separately by the caller via supabase.auth.updateUser. */
export async function completeRegistration(args: {
  intake: Record<string, unknown>;
  kid_pin: string | null;
  gdpr_consent: boolean;
}): Promise<{ ok: boolean; family_id: string; child_id: string; profile: StudentProfile }> {
  return apiFetch('/auth/v3/complete-registration', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

/** Add a sibling (parent-gated; needs its own DSGVO consent). */
export async function addChildV3(args: {
  intake: { name: string; klasse: number; gender: string };
  kid_pin: string | null;
  gdpr_consent: boolean;
}): Promise<{ ok: boolean; child_id: string }> {
  return parentFetch('/auth/v3/children', { method: 'POST', body: JSON.stringify(args) });
}

/** Switch active child (v3, bearer-token-based). */
export async function activateChildV3(child_id: string, pin?: string): Promise<{
  ok: boolean;
  child_id: string;
  profile: StudentProfile;
}> {
  return apiFetch('/auth/v3/activate-child', {
    method: 'POST',
    body: JSON.stringify({ child_id, pin: pin ?? null }),
  });
}

/** Parent-gate password re-check (v3). Stores the elevation token for the
 * next sensitive call (sent automatically as X-Parent-Elevation). */
export async function verifyParentGateV3(password: string, purpose: string): Promise<{
  ok: boolean;
  elevated_until: string | null;
  elevation_token: string | null;
}> {
  const res = await apiFetch<{ ok: boolean; elevated_until: string | null; elevation_token: string | null }>(
    '/auth/v3/verify-parent-gate',
    { method: 'POST', body: JSON.stringify({ password, purpose }) },
  );
  if (res.ok && res.elevation_token) {
    setParentElevation(res.elevation_token, res.elevated_until);
  }
  return res;
}

/** Update or clear a child's 4-digit PIN. Requires fresh parent-gate elevation. */
export async function updateChildPin(child_id: string, new_pin: string | null): Promise<{ ok: boolean }> {
  return apiFetch('/auth/v3/child-pin', {
    method: 'PUT',
    headers: elevatedHeaders(),
    body: JSON.stringify({ child_id, new_pin }),
  });
}

/** Patch a child's name and/or Klasse (1–6). Requires fresh parent-gate elevation. */
export async function updateChildProfile(
  child_id: string,
  patch: { name?: string; klasse?: number },
): Promise<{ ok: boolean }> {
  return apiFetch('/auth/v3/child-profile', {
    method: 'PATCH',
    headers: elevatedHeaders(),
    body: JSON.stringify({ child_id, ...patch }),
  });
}

/** DSGVO Art. 17 — self-serve account deletion. Requires fresh parent-gate elevation. */
export async function deleteAccountV3(password: string, confirm: string): Promise<{
  ok: boolean;
  families_deleted: number;
  children_deleted: number;
  pilot_events_deleted: number;
  question_exposure_deleted: number;
  flagged_questions_deleted: number;
}> {
  return apiFetch('/auth/v3/delete-account', {
    method: 'POST',
    headers: elevatedHeaders(),
    body: JSON.stringify({ password, confirm }),
  });
}

// ── Session ───────────────────────────────────────────────────────────

export async function startSession(): Promise<{
  exercise_count: number;
  page: PagePayload;
  xp_points: number;
  level: number;
  resumed: boolean;
  /** Exercises on the resumed page that were already answered. */
  answered_ids?: string[];
}> {
  return apiFetch('/session/start', { method: 'POST' });
}

/** A Wissens-Check (pre/post check of a taught topic) the child should take now. */
export interface WissensCheck {
  check_id: string;
  phase: 'pre' | 'post';
  title: string;
  /** Items without answers or hints; options already shuffled. */
  items: Exercise[];
  /** Item ids already answered (resume). */
  answered: string[];
}

export interface CheckAnswerResult {
  ok: boolean;
  done: boolean;
  answered: number;
  total: number;
  xp_awarded: number;
  xp_points: number;
}

export async function getCurrentCheck(): Promise<{ check: WissensCheck | null }> {
  return apiFetch('/check/current');
}

/** Store one check answer. The response never says whether it was right. */
export async function answerCheck(
  check: Pick<WissensCheck, 'check_id' | 'phase'>,
  exerciseId: string,
  answer: string,
): Promise<CheckAnswerResult> {
  return apiFetch('/check/answer', {
    method: 'POST',
    body: JSON.stringify({ check_id: check.check_id, phase: check.phase, exercise_id: exerciseId, answer }),
  });
}

export type SessionMood = 'happy' | 'ok' | 'meh' | 'sad' | 'skipped';

export async function setSessionMood(mood: SessionMood): Promise<{ mood: SessionMood }> {
  return apiFetch('/session/mood', {
    method: 'POST',
    body: JSON.stringify({ mood }),
  });
}

export async function logHintRequested(
  exerciseId: string,
  topic: string,
  tier: number,
): Promise<{ tier: number }> {
  return apiFetch('/session/hint_requested', {
    method: 'POST',
    body: JSON.stringify({ exercise_id: exerciseId, topic, tier }),
  });
}

// ── Exercises ─────────────────────────────────────────────────────────

export async function submitAnswer(args: {
  exercise_id: string;
  answer: string;
  time_taken: number;
}): Promise<SubmitResponse> {
  return apiFetch('/exercise/submit', {
    method: 'POST',
    body: JSON.stringify(args),
  });
}

export async function getNextPage(): Promise<{
  page: PagePayload;
  exercise_count: number;
  session_complete: boolean;
}> {
  return apiFetch('/exercise/next');
}

export async function flagExercise(args: {
  exercise_id: string;
  reason?: string;
}): Promise<{ ok: boolean }> {
  return apiFetch('/exercise/flag', {
    method: 'POST',
    body: JSON.stringify({ exercise_id: args.exercise_id, reason: args.reason ?? '' }),
  });
}

// ── Explainer ─────────────────────────────────────────────────────────

export async function triggerExplainer(topic_key: string): Promise<{ explanation: ConceptExplanation }> {
  return apiFetch('/explain/trigger', {
    method: 'POST',
    body: JSON.stringify({ topic_key }),
  });
}

// ── Report ────────────────────────────────────────────────────────────

export async function getReport(): Promise<{ report: SessionReport; profile: StudentProfile }> {
  return apiFetch('/report/');
}

// ── Profile ───────────────────────────────────────────────────────────

export async function getProfile(): Promise<{ profile: StudentProfile; topic_labels?: Record<string, string> }> {
  return apiFetch('/profile/');
}

export function exportProfileUrl(): string {
  return '/profile/export';
}

export function parentSummaryPdfUrl(): string {
  return '/parent/summary.pdf';
}

export function parentMilestoneUrl(chapterId: string): string {
  return `/parent/milestone/${encodeURIComponent(chapterId)}.png`;
}

export async function importProfile(file: File): Promise<{ ok: boolean; profile: StudentProfile }> {
  const formData = new FormData();
  formData.append('file', file);
  // FormData must set its own multipart boundary — strip Content-Type from
  // the elevated headers so fetch() generates one. Auth + X-Parent-Elevation
  // remain attached for the parent-gated /profile/import route.
  const headers = elevatedHeaders();
  delete (headers as Record<string, string>)['Content-Type'];
  const res = await fetch('/profile/import', {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(detail.detail ?? `API error ${res.status}`);
  }
  return res.json();
}

// ── Preferences ──────────────────────────────────────────────────────────

export async function getPreferences(): Promise<{ adventurer_id: string; preferences: KidPreferences; interests: string[] }> {
  return apiFetch('/preferences/');
}

/** Parent-only: update session length, difficulty, focus topics, interests.
 * Requires fresh parent-gate elevation. For child-driven adventurer swaps use
 * `updateAdventurerPreference`. */
export async function updatePreferences(patch: {
  adventurer_id?: string;
  companion_id?: string;
  session_length?: string;
  difficulty_preference?: string;
  focus_topics?: string[];
  interests?: string[];
}): Promise<{ adventurer_id: string; preferences: KidPreferences; interests: string[] }> {
  return apiFetch('/preferences/', {
    method: 'PUT',
    headers: elevatedHeaders(),
    body: JSON.stringify(patch),
  });
}

/** Child-safe: change adventurer / companion only. No parent-gate required. */
export async function updateAdventurerPreference(patch: {
  adventurer_id?: string;
  companion_id?: string;
}): Promise<{ adventurer_id: string; preferences: KidPreferences; interests: string[] }> {
  return apiFetch('/preferences/adventurer', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
}

// ── Parent Elternbereich ───────────────────────────────────────────────

export async function getParentAnalysis(): Promise<{ analysis: ProgressAnalysis }> {
  return parentFetch('/parent/analysis');
}

export async function refreshParentAnalysis(): Promise<{ analysis: ProgressAnalysis }> {
  return parentFetch('/parent/analysis/refresh', { method: 'POST' });
}

export async function sendParentChat(message: string): Promise<{ reply: string; history: ParentChatTurn[] }> {
  return parentFetch('/parent/chat', {
    method: 'POST',
    body: JSON.stringify({ message }),
  });
}

export async function getParentChatHistory(): Promise<{ history: ParentChatTurn[] }> {
  return parentFetch('/parent/chat');
}

export interface FlaggedEntry {
  exercise_id: string;
  topic: string;
  sub_skill_id?: string;
  question_text: string;
  correct_answer: string;
  wrong_options?: string[];
  reason: string;
  reported_at: string;
  student_name: string;
}

/** A child's reflection answer, as shown to parents. */
export interface ReflectionItem {
  date: string;
  topic: string;
  prompt: string;
  answer: string;
  escalate_urgent: boolean;
}

/** An urgent-help signal from Learn mode ("frage") or a reflection ("gedanke"). */
export interface AlertItem {
  source: 'frage' | 'gedanke';
  date: string;
  text: string;
  prompt: string;
}

/** Parent-only: urgent signals from the last days (dashboard banner, no email). */
export async function getParentAlerts(): Promise<{ items: AlertItem[]; window_days: number }> {
  return parentFetch('/parent/alerts');
}

/** Parent-only: the child's reflection answers, newest first. */
export async function getParentReflections(): Promise<{ items: ReflectionItem[] }> {
  return parentFetch('/parent/reflections');
}

export async function getFlaggedQuestions(limit = 20): Promise<{ entries: FlaggedEntry[] }> {
  // Parent-only: entries carry the answer key, so the gate token is required.
  return parentFetch(`/parent/flagged?limit=${encodeURIComponent(limit)}`);
}

// ── Learning Objectives ───────────────────────────────────────────────

export async function listObjectives(status = 'active'): Promise<{ objectives: LearningObjective[] }> {
  return apiFetch(`/objectives/?status=${status}`);
}

export async function postObjective(args: {
  raw_goal: string;
  deadline_iso?: string;
}): Promise<{ objective: LearningObjective }> {
  return apiFetch('/objectives/', {
    method: 'POST',
    headers: elevatedHeaders(),
    body: JSON.stringify(args),
  });
}

export async function patchObjective(
  id: string,
  status: string,
): Promise<{ objective: LearningObjective }> {
  return apiFetch(`/objectives/${id}`, {
    method: 'PATCH',
    headers: elevatedHeaders(),
    body: JSON.stringify({ status }),
  });
}

export async function deleteObjective(id: string): Promise<{ ok: boolean }> {
  return apiFetch(`/objectives/${id}`, { method: 'DELETE', headers: elevatedHeaders() });
}

// ── Learn Mode (Finn Q&A) ─────────────────────────────────────────────

export async function askFinn(question: string): Promise<LearnAskResponse> {
  return apiFetch<LearnAskResponse>('/learn/ask', {
    method: 'POST',
    body: JSON.stringify({ question }),
  });
}

export async function getLearnHistory(limit = 50): Promise<{
  items: LearnHistoryItem[];
  daily_cap: number;
  remaining_today: number;
}> {
  return apiFetch(`/learn/history?limit=${encodeURIComponent(limit)}`);
}

export async function deleteLearnPair(pairId: string): Promise<{ ok: boolean }> {
  return apiFetch(`/learn/history/${encodeURIComponent(pairId)}`, {
    method: 'DELETE', headers: elevatedHeaders(),
  });
}

export async function flagLearnPair(pairId: string, reason = ''): Promise<{ ok: boolean }> {
  return apiFetch(`/learn/history/${encodeURIComponent(pairId)}`, {
    method: 'PATCH',
    headers: elevatedHeaders(),
    body: JSON.stringify({ reason }),
  });
}

export async function getLearnSettings(): Promise<{ settings: LearnSettings }> {
  return apiFetch('/learn/settings');
}

export async function updateLearnSettings(
  patch: Partial<LearnSettings>,
): Promise<{ settings: LearnSettings }> {
  return apiFetch('/learn/settings', {
    method: 'PATCH',
    headers: elevatedHeaders(),
    body: JSON.stringify(patch),
  });
}

export async function startThementrainer(
  topic_label: string,
): Promise<{ topic_label: string | null; kickoff: string | null }> {
  return apiFetch('/learn/thementrainer', {
    method: 'POST',
    body: JSON.stringify({ topic_label }),
  });
}

export async function getThementrainer(): Promise<{ topic_label: string | null; kickoff: string | null }> {
  return apiFetch('/learn/thementrainer');
}

export async function stopThementrainer(): Promise<{ topic_label: string | null; kickoff: string | null }> {
  return apiFetch('/learn/thementrainer', { method: 'DELETE' });
}

